import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "moment/locale/es";
import "moment/locale/pt-br";
import {
  Col,
  Dropdown,
  DropdownMenu,
  DropdownToggle,
  Row,
  Spinner,
} from "reactstrap";
import classnames from "classnames";
import SimpleBar from "simplebar-react";

import bell from "../../assets/images/svg/bell.svg";
import {
  OPEN_STUDIO_SWITCHER_EVENT,
} from "../Studio/MembershipInviteWatcher";
import { dispatchSupportSummaryRefresh } from "../../helpers/supportTicketUx";
import {
  AppNotification,
  isNotificationUnread,
  listNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from "../../services/notificationService";

const NOTIFICATIONS_PER_PAGE = 20;

const LOW_STOCK_ACTION_URL = "/products";

const isMembershipInviteNotification = (notification: AppNotification): boolean =>
  notification.data?.kind === "membership_invite";

const LOW_STOCK_TYPE_ICON = {
  icon: "bx bx-package",
  bgClass: "bg-warning-subtle",
  textClass: "text-warning",
};

const CLIENT_EMAIL_BLOCKED_TYPE_ICON = {
  icon: "bx bx-envelope",
  bgClass: "bg-warning-subtle",
  textClass: "text-warning",
};

const TENANT_EMAIL_CIRCUIT_OPEN_TYPE_ICON = {
  icon: "bx bx-pause-circle",
  bgClass: "bg-danger-subtle",
  textClass: "text-danger",
};

const CATEGORY_ICON_MAP: Record<
  string,
  { icon: string; bgClass: string; textClass: string }
> = {
  billing: { icon: "bx bx-credit-card", bgClass: "bg-warning-subtle", textClass: "text-warning" },
  inventory: { icon: "bx bx-box", bgClass: "bg-danger-subtle", textClass: "text-danger" },
  appointment: { icon: "bx bx-calendar", bgClass: "bg-info-subtle", textClass: "text-info" },
  integration: { icon: "bx bx-plug", bgClass: "bg-primary-subtle", textClass: "text-primary" },
  system: { icon: "bx bx-badge-check", bgClass: "bg-info-subtle", textClass: "text-info" },
  team: { icon: "bx bx-group", bgClass: "bg-success-subtle", textClass: "text-success" },
  support: { icon: "bx bx-headphone", bgClass: "bg-primary-subtle", textClass: "text-primary" },
};

const DEFAULT_TYPE_ICON = {
  icon: "bx bx-bell",
  bgClass: "bg-secondary-subtle",
  textClass: "text-secondary",
};

const resolveMomentLocale = (language: string): string => {
  if (language === "pt-BR") {
    return "pt-br";
  }

  if (language === "es") {
    return "es";
  }

  return "en";
};

const isLowStockNotification = (notification: AppNotification): boolean =>
  notification.data?.kind === "low_stock";

const isExpiringInventoryNotification = (notification: AppNotification): boolean =>
  notification.data?.kind === "expiring_inventory" ||
  notification.data?.kind === "expired_inventory";

const isClientEmailBlockedNotification = (notification: AppNotification): boolean =>
  notification.data?.kind === "client_email_blocked";

const isTenantEmailCircuitOpenNotification = (notification: AppNotification): boolean =>
  notification.data?.kind === "tenant_email_circuit_open";

const resolveCategory = (notification: AppNotification): string => {
  const category = notification.data?.category;

  if (typeof category === "string" && category.trim() !== "") {
    return category.trim();
  }

  if (
    notification.data?.kind === "low_stock" ||
    notification.data?.kind === "expiring_inventory" ||
    notification.data?.kind === "expired_inventory"
  ) {
    return "inventory";
  }

  return "system";
};

const NotificationDropdown = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isNotificationDropdown, setIsNotificationDropdown] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [markingIds, setMarkingIds] = useState<Set<string>>(new Set());

  const unreadCount = useMemo(
    () => notifications.filter((notification) => isNotificationUnread(notification)).length,
    [notifications]
  );

  const fetchNotifications = useCallback(async () => {
    setLoading(true);

    try {
      const response = await listNotifications({ per_page: NOTIFICATIONS_PER_PAGE });
      setNotifications(response.data);

      const hasSupportUnread = (response.data || []).some(
        (notification) =>
          isNotificationUnread(notification) &&
          (notification.data?.category === "support" ||
            notification.data?.kind === "support_ticket_agent_reply")
      );

      if (hasSupportUnread) {
        dispatchSupportSummaryRefresh();
      }
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    const refreshNotifications = () => {
      void fetchNotifications();
    };

    window.addEventListener("adatis:notifications:refresh", refreshNotifications);

    return () => {
      window.removeEventListener("adatis:notifications:refresh", refreshNotifications);
    };
  }, [fetchNotifications]);

  useEffect(() => {
    if (isNotificationDropdown) {
      fetchNotifications();
    }
  }, [isNotificationDropdown, fetchNotifications]);

  const toggleNotificationDropdown = () => {
    setIsNotificationDropdown((previous) => !previous);
  };

  const formatRelativeTime = (createdAt: string): string => {
    moment.locale(resolveMomentLocale(i18n.language));

    return moment(createdAt).fromNow();
  };

  const resolveTypeIcon = (notification: AppNotification) => {
    if (isLowStockNotification(notification) || isExpiringInventoryNotification(notification)) {
      return LOW_STOCK_TYPE_ICON;
    }

    if (isClientEmailBlockedNotification(notification)) {
      return CLIENT_EMAIL_BLOCKED_TYPE_ICON;
    }

    if (isTenantEmailCircuitOpenNotification(notification)) {
      return TENANT_EMAIL_CIRCUIT_OPEN_TYPE_ICON;
    }

    return CATEGORY_ICON_MAP[resolveCategory(notification)] ?? DEFAULT_TYPE_ICON;
  };

  const resolveTitle = (notification: AppNotification): string => {
    const title = notification.data?.title;

    if (typeof title === "string" && title.trim() !== "") {
      return title.trim();
    }

    const titleKey = notification.data?.title_key;

    if (typeof titleKey === "string" && titleKey.trim() !== "") {
      return t(titleKey);
    }

    return t("header.notifications.untitled");
  };

  const resolveBody = (notification: AppNotification): string | null => {
    const body = notification.data?.body;

    if (typeof body === "string" && body.trim() !== "") {
      return body.trim();
    }

    const bodyKey = notification.data?.body_key;

    if (typeof bodyKey === "string" && bodyKey.trim() !== "") {
      const productName = notification.data?.product_name;
      const currentStock = notification.data?.current_stock;
      const minimumStock = notification.data?.minimum_stock;
      const productCount = notification.data?.product_count;
      const studioName = notification.data?.studio_name;

      return t(bodyKey, {
        name: typeof productName === "string" ? productName : "",
        current: typeof currentStock === "number" ? currentStock : 0,
        minimum: typeof minimumStock === "number" ? minimumStock : 0,
        count: typeof productCount === "number" ? productCount : 0,
        studio: typeof studioName === "string" ? studioName : "",
        client:
          typeof notification.data?.client_name === "string"
            ? notification.data.client_name
            : "",
        datetime:
          typeof notification.data?.start_datetime === "string"
            ? notification.data.start_datetime.slice(0, 16).replace("T", " ")
            : "",
      });
    }

    return null;
  };

  const resolveActionUrl = (notification: AppNotification): string | null => {
    const actionUrl = notification.data?.action_url;

    if (typeof actionUrl === "string" && actionUrl.trim() !== "") {
      return actionUrl.trim();
    }

    if (isLowStockNotification(notification)) {
      return LOW_STOCK_ACTION_URL;
    }

    return null;
  };

  const handleMarkAsRead = async (notification: AppNotification): Promise<void> => {
    if (!isNotificationUnread(notification) || markingIds.has(notification.id)) {
      return;
    }

    setMarkingIds((previous) => new Set(previous).add(notification.id));

    try {
      const response = await markNotificationAsRead(notification.id);
      setNotifications((previous) =>
        previous.map((item) =>
          item.id === notification.id ? response.data : item
        )
      );
    } catch {
      // Error surfaced by api_helper interceptor when applicable.
    } finally {
      setMarkingIds((previous) => {
        const next = new Set(previous);
        next.delete(notification.id);
        return next;
      });
    }
  };

  const handleNotificationClick = async (notification: AppNotification): Promise<void> => {
    const actionUrl = resolveActionUrl(notification);

    if (isNotificationUnread(notification)) {
      await handleMarkAsRead(notification);
    }

    if (
      notification.data?.category === "support" ||
      notification.data?.kind === "support_ticket_agent_reply"
    ) {
      dispatchSupportSummaryRefresh();
    }

    if (isMembershipInviteNotification(notification)) {
      setIsNotificationDropdown(false);
      const rawMembershipId = notification.data?.membership_id;
      const membershipId =
        typeof rawMembershipId === "number"
          ? rawMembershipId
          : typeof rawMembershipId === "string"
            ? Number(rawMembershipId)
            : NaN;
      window.dispatchEvent(
        new CustomEvent(OPEN_STUDIO_SWITCHER_EVENT, {
          detail: {
            membershipId: Number.isFinite(membershipId) ? membershipId : null,
          },
        })
      );
      return;
    }

    if (actionUrl) {
      setIsNotificationDropdown(false);
      navigate(actionUrl);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0 || markingAll) {
      return;
    }

    setMarkingAll(true);

    try {
      await markAllNotificationsAsRead();
      const readAt = new Date().toISOString();
      setNotifications((previous) =>
        previous.map((item) => ({
          ...item,
          read_at: item.read_at ?? readAt,
        }))
      );
    } catch {
      // Error surfaced by api_helper interceptor when applicable.
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <React.Fragment>
      <Dropdown
        isOpen={isNotificationDropdown}
        toggle={toggleNotificationDropdown}
        className="topbar-head-dropdown ms-1 header-item"
      >
        <DropdownToggle
          type="button"
          tag="button"
          className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle"
        >
          <i className="bx bx-bell fs-22"></i>
          {unreadCount > 0 && (
            <span className="position-absolute topbar-badge fs-10 translate-middle badge rounded-pill bg-danger">
              {unreadCount > 99 ? "99+" : unreadCount}
              <span className="visually-hidden">
                {t("header.notifications.unread_badge", { count: unreadCount })}
              </span>
            </span>
          )}
        </DropdownToggle>
        <DropdownMenu className="dropdown-menu-lg dropdown-menu-end p-0">
          <div className="dropdown-head bg-primary bg-pattern rounded-top">
            <div className="p-3">
              <Row className="align-items-center">
                <Col>
                  <h6 className="m-0 fs-16 fw-semibold text-white">
                    {t("header.notifications.title")}
                  </h6>
                </Col>
                {unreadCount > 0 && (
                  <Col xs="auto">
                    <span className="badge bg-light-subtle fs-13 text-body">
                      {t("header.notifications.unread_summary", { count: unreadCount })}
                    </span>
                  </Col>
                )}
              </Row>
            </div>
          </div>

          <div className="py-2 ps-2">
            {loading && notifications.length === 0 ? (
              <div className="py-4 text-center text-muted">
                <Spinner size="sm" className="me-2" />
                {t("header.notifications.loading")}
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-4">
                <div className="w-25 w-sm-50 pt-3 mx-auto">
                  <img src={bell} className="img-fluid" alt="" />
                </div>
                <div className="text-center pb-3 mt-2">
                  <h6 className="fs-18 fw-semibold lh-base">
                    {t("header.notifications.empty")}
                  </h6>
                </div>
              </div>
            ) : (
              <SimpleBar style={{ maxHeight: "300px" }} className="pe-2">
                {notifications.map((notification) => {
                  const typeIcon = resolveTypeIcon(notification);
                  const title = resolveTitle(notification);
                  const body = resolveBody(notification);
                  const isUnread = isNotificationUnread(notification);
                  const isMarking = markingIds.has(notification.id);

                  return (
                    <div
                      key={notification.id}
                      role="button"
                      tabIndex={0}
                      className={classnames(
                        "text-reset notification-item d-block dropdown-item position-relative",
                        { active: isUnread, disabled: isMarking }
                      )}
                      onClick={() => {
                        void handleNotificationClick(notification);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          void handleNotificationClick(notification);
                        }
                      }}
                    >
                      <div className="d-flex">
                        <div className="avatar-xs me-3 flex-shrink-0">
                          <span
                            className={classnames(
                              "avatar-title rounded-circle fs-16",
                              typeIcon.bgClass,
                              typeIcon.textClass
                            )}
                          >
                            <i className={typeIcon.icon}></i>
                          </span>
                        </div>
                        <div className="flex-grow-1">
                          <h6 className="mt-0 mb-1 fs-13 fw-semibold lh-base">
                            {title}
                          </h6>
                          {body && (
                            <div className="fs-13 text-muted">
                              <p className="mb-1">{body}</p>
                            </div>
                          )}
                          <p className="mb-0 fs-11 fw-medium text-uppercase text-muted">
                            <span>
                              <i className="mdi mdi-clock-outline"></i>{" "}
                              {formatRelativeTime(notification.created_at)}
                            </span>
                          </p>
                        </div>
                        {isUnread && (
                          <div className="px-2 fs-15 d-flex align-items-center">
                            <span className="badge bg-primary-subtle text-primary fs-10">
                              {t("header.notifications.new_badge")}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {unreadCount > 0 && (
                  <div className="my-3 text-center">
                    <button
                      type="button"
                      className="btn btn-soft-success waves-effect waves-light"
                      disabled={markingAll}
                      onClick={(event) => {
                        event.stopPropagation();
                        void handleMarkAllAsRead();
                      }}
                    >
                      {markingAll ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          {t("header.notifications.loading")}
                        </>
                      ) : (
                        <>
                          {t("header.notifications.mark_all_read")}{" "}
                          <i className="ri-arrow-right-line align-middle"></i>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </SimpleBar>
            )}
          </div>
        </DropdownMenu>
      </Dropdown>
    </React.Fragment>
  );
};

export default NotificationDropdown;
